<template>
    <div>
        <rive-animation
            v-if="animationsEnabled"
            ref="animation"
            src="/anim/chick.riv"
            alignment="bottomLeft"
            fit="fitWidth"
            :disable-rive-listeners="isTouchDevice"
            @load="onLoad"
        ></rive-animation>

        <img
            v-else
            src="/img/themes/easter/chick-fallback.svg"
            alt="Easter illustration of a chick"
        />
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import RiveAnimation from '@/vue/components/shared/anim/RiveAnimation';

@Component({
    components: {
        RiveAnimation,
    },
    data: function() {
        return {
            rive: undefined,
            riveJumpTrigger: undefined,
        };
    },
})
@AngularDirective('vrm-chick-animation')
export default class VrmChickAnimation extends Vue {
    @ngInject()
    AppearanceService;

    @Prop({ default: false })
    isMenuToggledOpen;

    /**
     * Watch for changes in the isMenuToggledOpen prop.
     *
     * If the menu is toggled open, fire the jump trigger.
     *
     * @param isMenuToggledOpen
     */
    @Watch('isMenuToggledOpen', { immediate: true })
    onMenuToggledOpenChange(isMenuToggledOpen) {
        if (this.rive && this.riveJumpTrigger && isMenuToggledOpen) {
            this.riveJumpTrigger.fire();
        }
    }

    /**
     * Called after the component and DOM have been initialized.
     *
     * Set up some listeners to handle resizing and mobile interactions properly.
     */
    mounted() {
        window.addEventListener('resize', this.onResize);

        if (this.isTouchDevice) {
            this.$refs.animation.$el.addEventListener('click', this.onClick);
        }
    }

    /**
     * Resize the Rive drawing surface if the window size changes.
     *
     * @param e
     */
    // eslint-disable-next-line no-unused-vars
    onResize(e) {
        if (this.rive) {
            this.rive.resizeDrawingSurfaceToCanvas();
        }
    }

    /**
     * Called on click on the Canvas.
     *
     * Used for mobile only since the default Rive listeners cause scrolling issues on touch devices.
     *
     * @param e
     */
    // eslint-disable-next-line no-unused-vars
    onClick(e) {
        if (this.riveJumpTrigger) {
            this.riveJumpTrigger.fire();
        }
    }

    /**
     * Called before the component has been destroyed.
     *
     * Removes the event listeners.
     */
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);

        if (this.isTouchDevice) {
            this.$refs.animation.$el.removeEventListener('click', this.onClick);
        }

        if (this.rive) {
            this.rive.cleanup();
        }
    }

    /**
     * Called when the Rive animation is loaded.
     *
     * Initialize any listeners needed for the animation.
     *
     * @param e
     */
    onLoad(e) {
        // Resize canvas to improve resolution.
        if (e.rive) {
            this.rive = e.rive;

            // Get the inputs via the name of the state machine
            const inputs = this.rive.stateMachineInputs('State Machine 1');

            // Find the input you want to set a value for, or trigger
            this.riveJumpTrigger = inputs.find(i => i.name === 'Jump');
        }
    }

    /**
     * Check if device is a touch device.
     *
     * @returns {boolean}
     */
    get isTouchDevice() {
        return (
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            navigator.msMaxTouchPoints > 0
        );
    }

    /**
     * Check if animations are enabled in the config.
     *
     * @returns {*|boolean}
     */
    get animationsEnabled() {
        return this.AppearanceService.isEasterThemeAnimationsActive();
    }
}
</script>
